import { useMemo } from 'react';
import { PriceLike, SalesforceProductSpecialPrice } from '~/lib/salesforce/types';
import { PriceTypes, TPrice } from '~/shared/components/ProductCard/components/Price';
import { useUtils } from '~/shared/hooks/useUtils';
import { ascertainNumber } from '~/shared/utils/numbers';
import { interpolateSalesforceText } from './utils';

type EvaluatedSpecialPrice = Omit<
    SalesforceProductSpecialPrice,
    'effectivePrice' | 'retailPrice'
> & {
    isActive: boolean;
    campaignText: string | undefined;
    effectivePrice: number;
    retailPrice: number;
    // TODO(IMC-1671): Replace with SalesforceProductPrice once types are aligned.
    prices: TPrice[] | undefined;
};

export const useSpecialPrice = (product: {
    id: string;
    c_prices?: PriceLike[];
    c_specialPrices?: SalesforceProductSpecialPrice;
    [key: string]: unknown;
}): EvaluatedSpecialPrice => {
    const { convertPriceToLocale } = useUtils();

    /**
     * TODO(IMC-1671): Drop this cast once the product and price types are aligned.
     * Callers hand us either shape; they only differ in which enum backs `type`.
     */
    const basePrices = (product.c_prices || []) as unknown as TPrice[];

    const {
        tileFooterText,
        staticTileFooterText,
        basketAmountThreshold,
        basketNumberOfProductsThreshold,
        isAmountThreshold,
        discountAmount,
        label: specialPriceLabel,
        effectivePrice,
        retailPrice,
        campaignElements,
    } = product.c_specialPrices || {};

    const shouldShowCampaignText = !!campaignElements?.includes('Campaign Text');

    const pricingCampaignText = useMemo(() => {
        if (!shouldShowCampaignText) return undefined;

        const text = staticTileFooterText || tileFooterText || '';

        const thresholdValue = isAmountThreshold
            ? convertPriceToLocale(basketAmountThreshold)
            : basketNumberOfProductsThreshold;

        return interpolateSalesforceText(text, [thresholdValue ?? 0, discountAmount ?? 0]);
    }, [product.id, staticTileFooterText, tileFooterText]);

    const shouldShowReducedPrices = !!campaignElements?.includes('Reduced Prices');

    const displayPrices = useMemo<TPrice[] | undefined>(() => {
        // We will use default pricing if there's no special pricing.
        if (!product.c_specialPrices || !shouldShowReducedPrices) return basePrices;

        const discountPrice: TPrice = {
            label: specialPriceLabel || '',
            value: ascertainNumber(effectivePrice, { parser: parseFloat }),
            type: PriceTypes.SALE,
        };

        // We will remove existing sale prices and replace it with our effective special price.
        const regularPrices = basePrices.filter((price) => price.type !== PriceTypes.SALE);

        return [...regularPrices, discountPrice];
    }, [effectivePrice, basePrices]);

    return {
        ...product.c_specialPrices,
        isActive: !!product.c_specialPrices,
        campaignText: pricingCampaignText,
        effectivePrice: ascertainNumber(effectivePrice, { parser: parseFloat }),
        retailPrice: ascertainNumber(retailPrice, { parser: parseFloat }),
        prices: displayPrices,
    };
};
